import { useState, useEffect, useCallback } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, addDoc, updateDoc, doc, query, orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/shared/UI/Button';
import { getFriendlyError } from '../../utils/formatters';

const DEAL_TYPES = [
  { value: 'equity', label: 'Equity' },
  { value: 'debt', label: 'Debt' },
  { value: 'revenue_share', label: 'Revenue Share' },
  { value: 'convertible_note', label: 'Convertible Note' },
];

const DEAL_TYPE_LABEL = DEAL_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

const PLACEHOLDER_HINT =
  'Available placeholders: [COMPANY_NAME], [FUND_NAME], [AMOUNT], ' +
  '[CURRENCY], [DATE], [INSTRUMENT_TERMS], [CONTACT_NAME], [REFERENCE]';

export default function TermSheetTemplatesPage() {
  const { user, userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'form' | 'preview'
  const [selected, setSelected] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'term_sheet_templates'), orderBy('deal_type'))
      );
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSeed() {
    setSeeding(true);
    setError('');
    try {
      const fns = getFunctions();
      const seedFn = httpsCallable(fns, 'seedTermSheetTemplates');
      await seedFn({});
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setSeeding(false);
    }
  }

  function openAdd() { setSelected(null); setView('form'); }
  function openEdit(t) { setSelected(t); setView('form'); }
  function openPreview(t) { setSelected(t); setView('preview'); }

  async function handleSaved() {
    setView('list');
    setSelected(null);
    await load();
  }

  if (view === 'form') {
    return (
      <TemplateForm
        template={selected}
        user={user}
        userProfile={userProfile}
        onSaved={handleSaved}
        onCancel={() => { setView('list'); setSelected(null); }}
      />
    );
  }

  if (view === 'preview' && selected) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 20 }}>
          <Button variant="ghost" onClick={() => { setView('list'); setSelected(null); }}>
            ← Back to Templates
          </Button>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          {selected.name}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
          {DEAL_TYPE_LABEL[selected.deal_type] || selected.deal_type}
          {' · '}Version {selected.version || 1}
        </p>
        <div className="card">
          <div className="card-body">
            <pre style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.7,
              margin: 0,
              color: 'var(--color-text)',
            }}>
              {selected.content}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // Group by deal type
  const grouped = DEAL_TYPES.map(dt => ({
    ...dt,
    items: templates.filter(t => t.deal_type === dt.value),
  }));

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Term Sheet Templates</h1>
          <p>Standard term sheet templates seeded per deal type. Placeholders are auto-filled from deal data when a term sheet is created.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {templates.length === 0 && !loading && (
            <Button variant="secondary" loading={seeding} onClick={handleSeed}>
              Seed Default Templates
            </Button>
          )}
          <Button variant="primary" onClick={openAdd}>+ Add Template</Button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <p className="text-muted" style={{ fontSize: 13 }}>Loading templates…</p>
      ) : templates.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              No term sheet templates exist yet. Click <strong>Seed Default Templates</strong> to
              create the four standard templates (equity, debt, revenue share, convertible note),
              or add one manually.
            </p>
          </div>
        </div>
      ) : (
        grouped.map(group => (
          <div key={group.value} style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {group.label}
            </h3>
            {group.items.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                No {group.label.toLowerCase()} templates.
              </p>
            ) : (
              group.items.map(t => (
                <div key={t.id} className="card mb-3">
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <p style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</p>
                      <span className="badge badge-default">{group.label}</span>
                      {t.is_active
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-warning">Inactive</span>}
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>v{t.version || 1}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="sm" variant="secondary" onClick={() => openPreview(t)}>Preview</Button>
                      <Button size="sm" variant="secondary" onClick={() => openEdit(t)}>Edit</Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ))
      )}

      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 8 }}>
        {PLACEHOLDER_HINT}
      </p>
    </div>
  );
}

function TemplateForm({ template, user, userProfile, onSaved, onCancel }) {
  const isEdit = !!template;
  const [name, setName] = useState(template?.name || '');
  const [dealType, setDealType] = useState(template?.deal_type || 'equity');
  const [content, setContent] = useState(template?.content || '');
  const [isActive, setIsActive] = useState(
    template ? template.is_active !== false : true
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    if (!name.trim()) { setError('Template name is required.'); return; }
    if (!content.trim()) { setError('Template content is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const performedByName =
        userProfile?.full_name || userProfile?.display_name || user?.email || '';
      if (isEdit) {
        await updateDoc(doc(db, 'term_sheet_templates', template.id), {
          name: name.trim(),
          deal_type: dealType,
          content,
          is_active: isActive,
          version: (template.version || 1) + 1,
          last_edited_at: new Date().toISOString(),
          last_edited_by: user?.uid || null,
          last_edited_by_name: performedByName,
        });
      } else {
        await addDoc(collection(db, 'term_sheet_templates'), {
          name: name.trim(),
          deal_type: dealType,
          content,
          is_active: isActive,
          version: 1,
          created_at: new Date().toISOString(),
          created_by: user?.uid || null,
          created_by_name: performedByName,
        });
      }
      await onSaved();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: 20 }}>
        <Button variant="ghost" onClick={onCancel}>← Back to Templates</Button>
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
        {isEdit ? `Edit: ${template.name}` : 'New Term Sheet Template'}
      </h2>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Template Name <span className="required">*</span></label>
            <input
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Equity Term Sheet"
            />
          </div>

          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Deal Type <span className="required">*</span></label>
            <select
              className="form-input"
              value={dealType}
              onChange={e => setDealType(e.target.value)}
            >
              {DEAL_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Template Content <span className="required">*</span></label>
            <textarea
              className="form-textarea"
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={22}
              style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
              placeholder="Enter the term sheet body with placeholders like [COMPANY_NAME]…"
            />
            <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 6 }}>
              {PLACEHOLDER_HINT}
            </p>
          </div>

          <div className="form-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
              />
              <span style={{ fontSize: 14 }}>Active (available for use on deals)</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex-between">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>
          {isEdit ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
}
